import { useEffect, useMemo, useState } from 'react';
import { Pencil, Save } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import {
  useActivityTargetsSetup,
  useSaveActivityDefaults,
  useSavePersonTargets,
  type ActivityMetric,
  type ActivityPeriod,
  type ActivityTargetRow,
  type ActivityTargetsSetup,
} from '../hooks/useActivityTargets';

// Settings > HR > Activity Targets: how many calls, meetings and follow-ups the sales team should get through.
// A company default applies to everyone in the sales departments; a person can have their own or be left out.

const PERIOD_UNIT: Record<ActivityPeriod, string> = { day: 'a day', week: 'a week', month: 'a month' };
const fieldClass =
  'rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

const describe = (row: { value: number; period: ActivityPeriod }) => `${row.value} ${PERIOD_UNIT[row.period]}`;

type Setup = ActivityTargetsSetup;

// ----------------------------------------------------------------- defaults

interface DefaultDraft {
  value: string;
  period: ActivityPeriod;
}

function DefaultsCard({ setup }: { setup: Setup }) {
  const save = useSaveActivityDefaults();
  const initial = useMemo(() => {
    const draft = {} as Record<ActivityMetric, DefaultDraft>;
    for (const m of setup.metrics) {
      const row = setup.defaults.find((d) => d.metric === m.metric);
      draft[m.metric] = { value: row ? String(row.value) : '', period: row?.period ?? 'day' };
    }
    return draft;
  }, [setup]);
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const invalid = setup.metrics.some((m) => {
    const v = draft[m.metric].value.trim();
    return v !== '' && (!/^\d+$/.test(v) || Number(v) > 100000);
  });

  const submit = () =>
    save.mutate(
      setup.metrics
        .map((m) => ({ metric: m.metric, period: draft[m.metric].period, value: Number(draft[m.metric].value || 0) }))
        .filter((t) => t.value > 0)
    );

  return (
    <Card>
      <CardHeader
        title="Company defaults"
        subtitle={`Applies to everyone in: ${setup.salesDepartments.join(', ') || 'no department'}. Leave a target empty for no default.`}
        action={
          <Button size="sm" onClick={submit} disabled={!dirty || invalid} loading={save.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> Save defaults
          </Button>
        }
      />
      <CardBody>
        <ul className="divide-y divide-border/50">
          {setup.metrics.map((m) => (
            <li key={m.metric} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{m.label}</p>
                <p className="text-xs text-muted-foreground">{m.hint}</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  aria-label={`${m.label} target`}
                  inputMode="numeric"
                  placeholder="None"
                  value={draft[m.metric].value}
                  onChange={(e) => setDraft((d) => ({ ...d, [m.metric]: { ...d[m.metric], value: e.target.value } }))}
                  className={`${fieldClass} w-24 text-right`}
                />
                <select
                  aria-label={`${m.label} period`}
                  value={draft[m.metric].period}
                  onChange={(e) => setDraft((d) => ({ ...d, [m.metric]: { ...d[m.metric], period: e.target.value as ActivityPeriod } }))}
                  className={fieldClass}
                >
                  {setup.periods.map((p) => (
                    <option key={p.period} value={p.period}>
                      per {p.period}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
        {invalid && <p className="mt-2 text-xs text-red-600">Targets must be whole numbers.</p>}
      </CardBody>
    </Card>
  );
}

// ------------------------------------------------------------------- people

type Mode = 'default' | 'custom' | 'exempt';
interface PersonDraft {
  mode: Mode;
  value: string;
  period: ActivityPeriod;
}

function PersonModal({ setup, person, onClose }: { setup: Setup; person: Setup['people'][number]; onClose: () => void }) {
  const save = useSavePersonTargets();
  const start = () => {
    const draft = {} as Record<ActivityMetric, PersonDraft>;
    for (const m of setup.metrics) {
      const own = person.overrides.find((o) => o.metric === m.metric);
      const base = setup.defaults.find((d) => d.metric === m.metric);
      draft[m.metric] = own
        ? { mode: own.exempt || own.value === 0 ? 'exempt' : 'custom', value: own.value > 0 ? String(own.value) : '', period: own.period }
        : { mode: 'default', value: base ? String(base.value) : '', period: base?.period ?? 'day' };
    }
    return draft;
  };
  const [draft, setDraft] = useState(start);
  const set = (metric: ActivityMetric, patch: Partial<PersonDraft>) => setDraft((d) => ({ ...d, [metric]: { ...d[metric], ...patch } }));
  const invalid = setup.metrics.some((m) => draft[m.metric].mode === 'custom' && !/^[1-9]\d*$/.test(draft[m.metric].value.trim()));

  const submit = () => {
    const overrides: ActivityTargetRow[] = [];
    for (const m of setup.metrics) {
      const d = draft[m.metric];
      if (d.mode === 'custom') overrides.push({ metric: m.metric, period: d.period, value: Number(d.value) });
      else if (d.mode === 'exempt') overrides.push({ metric: m.metric, period: d.period, value: 0, exempt: true });
    }
    save.mutate({ userId: person.id, overrides }, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Activity targets: ${person.name}`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={invalid} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <ul className="divide-y divide-border/50">
        {setup.metrics.map((m) => {
          const d = draft[m.metric];
          const base = setup.defaults.find((x) => x.metric === m.metric);
          return (
            <li key={m.metric} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <p className="text-sm font-medium text-foreground">{m.label}</p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label={`${m.label} for ${person.name}`}
                  value={d.mode}
                  onChange={(e) => set(m.metric, { mode: e.target.value as Mode })}
                  className={fieldClass}
                >
                  <option value="default">{base ? `Company default (${describe(base)})` : 'Company default (none)'}</option>
                  <option value="custom">Their own target</option>
                  <option value="exempt">Does not apply</option>
                </select>
                {d.mode === 'custom' && (
                  <>
                    <input
                      aria-label={`${m.label} own target`}
                      inputMode="numeric"
                      value={d.value}
                      onChange={(e) => set(m.metric, { value: e.target.value })}
                      className={`${fieldClass} w-20 text-right`}
                    />
                    <select
                      aria-label={`${m.label} own period`}
                      value={d.period}
                      onChange={(e) => set(m.metric, { period: e.target.value as ActivityPeriod })}
                      className={fieldClass}
                    >
                      {setup.periods.map((p) => (
                        <option key={p.period} value={p.period}>
                          per {p.period}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function cellFor(person: Setup['people'][number], metric: ActivityMetric) {
  const own = person.overrides.find((o) => o.metric === metric);
  if (own && (own.exempt || own.value === 0)) return <span className="text-xs text-muted-foreground">Does not apply</span>;
  const eff = person.effective.find((e) => e.metric === metric);
  if (!eff) return <span className="text-muted-foreground">-</span>;
  return (
    <span className={eff.source === 'person' ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
      {describe(eff)}
      {eff.source === 'person' && <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">own</span>}
    </span>
  );
}

function PeopleCard({ setup }: { setup: Setup }) {
  const [editing, setEditing] = useState<Setup['people'][number] | null>(null);
  return (
    <Card>
      <CardHeader title="People" subtitle="What each person is working to. Edit to give someone their own targets, or leave a metric out for them." />
      <CardBody>
        {setup.people.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">There is nobody in a sales department yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-3 font-semibold">Person</th>
                  {setup.metrics.map((m) => (
                    <th key={m.metric} className="px-3 py-2 font-semibold">
                      {m.label}
                    </th>
                  ))}
                  <th className="py-2 pl-3" />
                </tr>
              </thead>
              <tbody>
                {setup.people.map((p) => (
                  <tr key={p.id} className="border-b border-border/40 last:border-0">
                    <td className="py-2.5 pr-3">
                      <p className="font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{[p.departmentRole, p.department].filter(Boolean).join(' · ')}</p>
                    </td>
                    {setup.metrics.map((m) => (
                      <td key={m.metric} className="px-3 py-2.5">
                        {cellFor(p, m.metric)}
                      </td>
                    ))}
                    <td className="py-2.5 pl-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(p)} aria-label={`Edit targets for ${p.name}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      {editing && <PersonModal setup={setup} person={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

export function HrActivityTargetsPage() {
  const { data, isLoading, isError } = useActivityTargetsSetup();
  if (isLoading) return <Skeleton className="h-96" />;
  if (isError || !data) return <p className="text-sm text-red-600">Could not load the activity targets.</p>;
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Activity Targets</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          How many calls, meetings and follow-ups the sales team should get through. A daily target is paused on weekends, company holidays and approved
          leave days. Revenue targets and incentives are set under People &gt; Incentives.
        </p>
      </div>
      <DefaultsCard setup={data} />
      <PeopleCard setup={data} />
    </div>
  );
}
